/**
 * Task V2 Repository - GraphQL Mutations
 *
 * Mutation definitions for Task primitive.
 * 7 mutations matching backend task_graphql.rs.
 *
 * @see graphql_gateway/src/task_api/task_graphql.rs - Backend resolvers
 */

import { gql } from '@apollo/client';
import { TASK_FIELDS_FRAGMENT } from './fragments';

/** Create a new task */
export const CREATE_TASK = gql`
  mutation CreateTask($tenantId: String!, $input: GqlCreateTaskInput!) {
    createTask(tenantId: $tenantId, input: $input) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Assign task to actor */
export const ASSIGN_TASK = gql`
  mutation AssignTask($tenantId: String!, $input: GqlAssignTaskInput!) {
    assignTask(tenantId: $tenantId, input: $input) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Start task execution */
export const START_TASK = gql`
  mutation StartTask($tenantId: String!, $taskId: String!) {
    startTask(tenantId: $tenantId, taskId: $taskId) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Update task progress */
export const UPDATE_TASK_PROGRESS = gql`
  mutation UpdateTaskProgress(
    $tenantId: String!
    $input: GqlUpdateProgressInput!
  ) {
    updateTaskProgress(tenantId: $tenantId, input: $input) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Complete task with output */
export const COMPLETE_TASK = gql`
  mutation CompleteTask($tenantId: String!, $input: GqlCompleteTaskInput!) {
    completeTask(tenantId: $tenantId, input: $input) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Fail task with error */
export const FAIL_TASK = gql`
  mutation FailTask($tenantId: String!, $input: GqlFailTaskInput!) {
    failTask(tenantId: $tenantId, input: $input) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/** Cancel task */
export const CANCEL_TASK = gql`
  mutation CancelTask($tenantId: String!, $taskId: String!, $reason: String!) {
    cancelTask(tenantId: $tenantId, taskId: $taskId, reason: $reason) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;

/**
 * Transition task to new state (Pure Append-Only Architecture)
 *
 * Creates NEW task version with:
 * - Incremented version number
 * - parent_id linking to current task
 * - root_task_id preserved from V1
 * - Updated status and assigned_actor_id (for reassignments)
 * - Appended reassignment_history (if actor changed)
 *
 * @example
 * ```typescript
 * // Task reassignment (Actor1 → Actor2)
 * const newTask = await client.mutate<Record<string, any>>({
 *   mutation: TRANSITION_TASK,
 *   variables: {
 *     tenantId: "healthcare-tenant",
 *     workflowInstanceId: "wf_inst_123",
 *     taskId: "task_abc",
 *     input: {
 *       newStatus: "ASSIGNED",
 *       trigger: "load_balancer_reassigned",
 *       newAssignedActorId: "actor-002"
 *     }
 *   }
 * });
 * // newTask.version = currentTask.version + 1
 * // newTask.parentId = currentTask.id
 * // newTask.rootTaskId = currentTask.rootTaskId
 * // newTask.reassignmentHistory.push({from: actor-001, to: actor-002, ...})
 * ```
 */
export const TRANSITION_TASK = gql`
  mutation TransitionTask(
    $tenantId: String!
    $workflowInstanceId: String!
    $taskId: String!
    $input: TransitionTaskInput!
  ) {
    transitionTask(
      tenantId: $tenantId
      workflowInstanceId: $workflowInstanceId
      taskId: $taskId
      input: $input
    ) {
      ...TaskFields
    }
  }
  ${TASK_FIELDS_FRAGMENT}
`;
